// src/lib/matchingArchetypes.js
//
// Moteur d'archétypes de collectionneur — porté depuis ArtkivVisionApp
// (src/questionnaire/archetypes.ts, deriveArchetype). C'est la seule logique
// de classification qui existe réellement dans le produit ; ce module est la
// source canonique côté artkiv-app, importable aussi bien depuis le serveur
// (via require) que depuis le front (via import — interop CommonJS/webpack).
//
// Classement déterministe : chaque archétype a un prototype sur les 9
// dimensions psychologiques (0-100), on classe par cosinus (direction du
// profil par rapport au prototype, centré en 50 = neutre).

const ARCHETYPE_DIMENSIONS = [
  "heritage",
  "innovation",
  "plaisir",
  "esthetique",
  "conceptuel",
  "emotionnel",
  "social",
  "individuel",
  "investissement",
];

const ARCHETYPE_PROTOTYPES = {
  batisseur: {
    heritage: 50, innovation: 70, plaisir: 30, esthetique: 50, conceptuel: 50,
    emotionnel: 20, social: 50, individuel: 60, investissement: 95,
  },
  passionne: {
    heritage: 50, innovation: 50, plaisir: 90, esthetique: 80, conceptuel: 50,
    emotionnel: 95, social: 50, individuel: 70, investissement: 20,
  },
  connecteur: {
    heritage: 50, innovation: 60, plaisir: 50, esthetique: 50, conceptuel: 75,
    emotionnel: 50, social: 95, individuel: 65, investissement: 50,
  },
  gardien: {
    heritage: 95, innovation: 30, plaisir: 50, esthetique: 50, conceptuel: 65,
    emotionnel: 50, social: 70, individuel: 35, investissement: 50,
  },
  mecene: {
    heritage: 50, innovation: 50, plaisir: 50, esthetique: 50, conceptuel: 50,
    emotionnel: 60, social: 90, individuel: 15, investissement: 25,
  },
};

// Contenu affichable (FR) — copié de vision.artkiv.com (i18n `vision.archetype.<key>.*`).
const ARCHETYPE_CONTENT = {
  batisseur: {
    name: "Le Bâtisseur",
    tagline: "Vous lisez l'art comme un portefeuille : cotes, carrières, tendances.",
    risk: "Une documentation centrée sur la valeur de revente, qui néglige provenance, état et contexte curatorial.",
    need: "Tracer provenance et conservation — ce qui justifiera la valeur au-delà de la cote.",
  },
  passionne: {
    name: "Le Passionné",
    tagline: "Vous achetez au coup de cœur ; votre collection raconte une histoire intime.",
    risk: "Peu de traçabilité, des acquisitions dispersées, aucune vue d'ensemble pour vos héritiers.",
    need: "Construire la vue d'ensemble et le mode d'emploi de la collection.",
  },
  connecteur: {
    name: "Le Connecteur",
    tagline: "Vous collectionnez au fil des rencontres : artistes, curateurs, galeristes.",
    risk: "Une documentation qui vit dans votre mémoire et vos relations, pas sur le papier.",
    need: "Coucher sur le papier les provenances et les liens qui font la collection.",
  },
  gardien: {
    name: "Le Gardien",
    tagline: "Vous pensez déjà legs, institution, fondation — la transmission vous habite.",
    risk: "Une intention forte, mais rarement formalisée en dossier juridique et fiscal réel.",
    need: "Transformer l'intention en dossier de transmission constitué.",
  },
  mecene: {
    name: "Le Mécène",
    tagline: "Vous collectionnez pour soutenir : artistes, institutions, causes.",
    risk: "Une collection dispersée (dons, prêts, financements) dont la valeur patrimoniale globale devient illisible.",
    need: "Consolider une valorisation lisible malgré la dispersion.",
  },
};

const ARCHETYPE_KEYS = Object.keys(ARCHETYPE_PROTOTYPES);

function clamp100(n) {
  return Math.max(0, Math.min(100, n));
}

// Recalibrage 08/10/2026 : affinité = max(0, cosinus) × 100 (miroir de Vision archetypes.ts) ;
// le classement se fait sur le cosinus brut, l'archétype principal est donc inchangé.
function cosineTo(profile, proto) {
  let dot = 0;
  let normV = 0;
  let normP = 0;
  for (const d of ARCHETYPE_DIMENSIONS) {
    const v = (typeof profile[d] === "number" ? profile[d] : 50) - 50;
    const p = proto[d] - 50;
    dot += v * p;
    normV += v * v;
    normP += p * p;
  }
  if (normV === 0 || normP === 0) return 0; // aucune information → 0 % partout, ambigu
  return dot / (Math.sqrt(normV) * Math.sqrt(normP));
}

// Classe un profil (9 dimensions 0-100, dims manquantes = 50 neutre) →
// { primary, confidence, ranking, content: { name, tagline, risk, need } }.
function deriveArchetype(profile) {
  const ranking = ARCHETYPE_KEYS.map((key) => {
    const cos = cosineTo(profile, ARCHETYPE_PROTOTYPES[key]);
    return { key, cos, affinity: Math.round(clamp100(100 * Math.max(0, cos))) };
  })
    .sort((a, b) => b.cos - a.cos)
    .map(({ key, affinity }) => ({ key, affinity }));

  const confidence = ranking.length > 1 ? clamp100(ranking[0].affinity - ranking[1].affinity) : 100;
  const primary = ranking[0].key;

  return { primary, confidence, ranking, content: ARCHETYPE_CONTENT[primary] };
}

module.exports = {
  ARCHETYPE_DIMENSIONS,
  ARCHETYPE_PROTOTYPES,
  ARCHETYPE_CONTENT,
  ARCHETYPE_KEYS,
  deriveArchetype,
};
